import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, unref as _unref, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "container",
  style: {"margin-top":"80px","padding-top":"40px"}
}
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "col-lg-6" }
const _hoisted_4 = { style: {"font-size":"2em","font-weight":"300","color":"#33475b","margin":"0"} }
const _hoisted_5 = { class: "col-lg-6 text-right align-self-end" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = { class: "form-group mb-4" }
const _hoisted_8 = { class: "error" }
const _hoisted_9 = { class: "form-group mb-4" }
const _hoisted_10 = { class: "form-row" }
const _hoisted_11 = { class: "form-group mb-4 col" }
const _hoisted_12 = ["value"]
const _hoisted_13 = { class: "form-group mb-4 col" }

import { computed, onMounted, ref, watch, type Ref } from 'vue'
    import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
    import { EditorState, generateName } from '@common/Common'
    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import { guid } from '@common/Guid'
    import { CurrencyDenomination, MonthYear, MonthYearHelpers, ValidationError } from '@common/types'
    import { useMarginCogsMetricConfigurationQueryV1 } from '@Quest/Metrics/queries-vue-composables'
    import { useCreateMarginCogsMetricCommandV1, useUpdateMarginCogsMetricCommandV1 } from '@Quest/Metrics/commands-vue-composables'
    import type { MarginCogsMetricConfigurationReadModel } from '@Quest/Metrics/readmodels'
    import { ValueCommitmentType } from '@Quest/Metrics/types'
    import MetricTimeWindowSettings from '@Quest/Metrics/Components/MetricTimeWindowSettings.vue'
    import MetricScheduleDependentSettings from '@Quest/Metrics/Components/MetricScheduleDependentSettings.vue'

    
export default /*@__PURE__*/_defineComponent({
  __name: 'MarginCogsMetricConfigurationPage',
  setup(__props) {

    const route = useRoute()
    const router = useRouter()

    const workspaceId = computed(() => Number(route.params?.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params?.strategyId ?? ''))
    const pageMode = computed(() => route.meta.mode as EditorState)
    const isEditMode = computed(() => pageMode.value === EditorState.Editor)

    const {
        runMarginCogsMetricConfigurationQueryV1,
        modelMarginCogsMetricConfigurationQueryV1
    } = useMarginCogsMetricConfigurationQueryV1(workspaceId, strategyId)

    const rm = modelMarginCogsMetricConfigurationQueryV1 as Ref<MarginCogsMetricConfigurationReadModel | null>

    const {
        invokeCreateMarginCogsMetricCommandV1,
        validationErrorsCreateMarginCogsMetricCommandV1,
        onSucceedCreateMarginCogsMetricCommandV1
    } = useCreateMarginCogsMetricCommandV1(workspaceId, strategyId)

    const {
        invokeUpdateMarginCogsMetricCommandV1,
        validationErrorsUpdateMarginCogsMetricCommandV1,
        onSucceedUpdateMarginCogsMetricCommandV1
    } = useUpdateMarginCogsMetricCommandV1(workspaceId, strategyId)


    const initialized = ref(false)
    const errors = ref<ValidationError[]>([])
    const isSaving = ref(false)
    const allowRouteLeave = ref(false)
    const isScheduleDependent = ref(false)

    const name = ref<string | null>(null)
    const description = ref<string | null>(null)
    const revenueId = ref<string | null>(null)
    const denomination = ref<CurrencyDenomination>(CurrencyDenomination.Unit)
    const windowStartOffsetInMonths = ref<number | null>(null)
    const windowLengthInMonths = ref<number | null>(null)

    const startDateBoundary = ref<MonthYear | null>(null)
    const endDateBoundary = ref<MonthYear | null>(null)
    const defaultProjectStartDate = ref<MonthYear | null>(null)

    const applyBenefitsConfidenceFactor = ref(false)
    const valueCommitmentType = ref<ValueCommitmentType>(ValueCommitmentType.Total)

    const pageHeading = computed(() => isEditMode.value ? 'Edit the metric' : 'New margin COGS metric')
    const saveButtonText = computed(() => isSaving.value ? 'Saving...' : 'Save changes')
    const nameError = computed(() => errors.value.find(e => e.ErrorCode.startsWith('Name.'))?.Error ?? null)

    const hasChanges = computed(() =>
    {
        if (!isEditMode.value) return true

        const model = rm.value
        if (!model) return true

        const scheduleEnabled = isScheduleDependent.value
        const scheduleExists = model.ScheduleDependentSettings != null

        if (scheduleEnabled !== scheduleExists)
            return true

        // base fields (common)
        if (
            name.value !== model.Name ||
            description.value !== model.Description ||
            revenueId.value !== model.AccountId ||
            denomination.value !== model.Denomination ||
            windowStartOffsetInMonths.value !== model.WindowStartOffsetInMonths ||
            windowLengthInMonths.value !== model.WindowLengthInMonths
        )
        {
            return true
        }

        if (!scheduleEnabled)
            return false

        const s = model.ScheduleDependentSettings

        return (
            applyBenefitsConfidenceFactor.value !== s?.ApplyBenefitsConfidenceFactor ||
            valueCommitmentType.value !== s?.ValueCommitmentType ||
            !MonthYearHelpers.areEqual(defaultProjectStartDate.value, s?.DefaultProjectStartDate) ||
            !MonthYearHelpers.areEqual(startDateBoundary.value, s?.StartDateBoundary) ||
            !MonthYearHelpers.areEqual(endDateBoundary.value, s?.EndDateBoundary)
        )
    })

    function handleSave()
    {
        if (!rm.value && isEditMode.value) return

        isSaving.value = true
        errors.value = []

        const isUpdate = isEditMode.value && rm.value

        const payload = {
            id: isUpdate ? rm.value!.Id : guid(),
            name: name.value,
            description: description.value,
            revenueId: revenueId.value,
            denomination: denomination.value,
            windowStartOffsetInMonths: windowStartOffsetInMonths.value,
            windowLengthInMonths: windowLengthInMonths.value,
            isScheduleDependent: isScheduleDependent.value,
            defaultProjectStartDate: defaultProjectStartDate.value,
            startDateBoundary: startDateBoundary.value,
            endDateBoundary: endDateBoundary.value,
            valueCommitmentType: valueCommitmentType.value,
            applyBenefitsConfidenceFactor: applyBenefitsConfidenceFactor.value
        }

        if (isUpdate)
        {
            invokeUpdateMarginCogsMetricCommandV1(payload)
        } else
        {
            invokeCreateMarginCogsMetricCommandV1(payload)
        }
    }

    function handleCancel()
    {
        if (isEditMode.value) router.go(0)
        else router.go(-1)
    }

    function handleBack()
    {
        router.go(-1)
    }

    onMounted(() =>
    {
        document.title = 'Margin COGS Metric Configuration'
        runMarginCogsMetricConfigurationQueryV1({ MetricId: route.params.metricId ?? null })
    })

    watch(rm, (model) =>
    {
        if (!model || initialized.value) return

        name.value = isEditMode.value ? model.Name : generateName(model.Name)
        description.value = model.Description
        revenueId.value = model.AccountId ?? null
        denomination.value = model.Denomination
        windowStartOffsetInMonths.value = model.WindowStartOffsetInMonths
        windowLengthInMonths.value = model.WindowLengthInMonths

        isScheduleDependent.value = model.ScheduleDependentSettings != null
        applyBenefitsConfidenceFactor.value = model.ScheduleDependentSettings?.ApplyBenefitsConfidenceFactor ?? false
        valueCommitmentType.value = model.ScheduleDependentSettings?.ValueCommitmentType ?? ValueCommitmentType.Total

        defaultProjectStartDate.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.DefaultProjectStartDate)
        startDateBoundary.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.StartDateBoundary)
        endDateBoundary.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.EndDateBoundary)

        initialized.value = true
    })

    onSucceedCreateMarginCogsMetricCommandV1(() =>
    {
        allowRouteLeave.value = true
        router.go(-1)
    })

    onSucceedUpdateMarginCogsMetricCommandV1(() =>
    {
        allowRouteLeave.value = true
        router.go(0)
    })

    watch(validationErrorsCreateMarginCogsMetricCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return
        errors.value = validationErrors
        isSaving.value = false
    })

    watch(validationErrorsUpdateMarginCogsMetricCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return
        errors.value = validationErrors
        isSaving.value = false
    })

    onBeforeRouteLeave((to, from, next) =>
    {
        if (allowRouteLeave.value)
        {
            next()
            return
        }

        if (hasChanges.value && !isSaving.value)
        {
            if (confirm('You have unsaved changes. Do you want to save them before leaving?'))
            {
                handleSave()
            }
            else
            {
                next()
            }
        } else
        {
            next()
        }
    })

return (_ctx: any,_cache: any) => {
  const _component_MonthPickerDialog = _resolveComponent("MonthPickerDialog")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_component_MonthPickerDialog, {
      viewModel: _ctx.startDateBoundaryMonthPicker,
      onUpdateDate: _cache[0] || (_cache[0] = ($event: any) => (startDateBoundary.value = $event))
    }, null, 8 /* PROPS */, ["viewModel"]),
    _createVNode(_component_MonthPickerDialog, {
      viewModel: _ctx.endDateBoundaryMonthPicker,
      onUpdateDate: _cache[1] || (_cache[1] = ($event: any) => (endDateBoundary.value = $event))
    }, null, 8 /* PROPS */, ["viewModel"]),
    _createVNode(_component_MonthPickerDialog, {
      viewModel: _ctx.defaultProjectStartDateMonthPicker,
      onUpdateDate: _cache[2] || (_cache[2] = ($event: any) => (defaultProjectStartDate.value = $event))
    }, null, 8 /* PROPS */, ["viewModel"]),
    _createVNode(CorporateNavigation, { ActivePage: "metrics" }),
    (initialized.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("h6", null, [
                _createElementVNode("a", {
                  class: "text-dark",
                  onClick: handleBack,
                  "data-testid": "margin-cogs-metric-config-back"
                }, [...(_cache[15] || (_cache[15] = [
                  _createElementVNode("i", {
                    class: "fa fa-angle-double-left",
                    "aria-hidden": "true"
                  }, null, -1 /* CACHED */),
                  _createTextVNode(" Back ", -1 /* CACHED */)
                ]))])
              ]),
              _createElementVNode("h1", _hoisted_4, _toDisplayString(pageHeading.value), 1 /* TEXT */)
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("button", {
                type: "submit",
                form: "metric-configuration-form",
                class: _normalizeClass(["mr-1", hasChanges.value ? 'btn btn-warning' : 'btn btn-outline-secondary']),
                disabled: isSaving.value,
                "data-testid": "margin-cogs-metric-config-save",
                style: {"padding":"0.7em 2em"}
              }, _toDisplayString(saveButtonText.value), 11 /* TEXT, CLASS, PROPS */, _hoisted_6),
              (hasChanges.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "btn btn-outline-secondary",
                    style: {"padding":"0.7em 2em"},
                    onClick: handleCancel,
                    "data-testid": "margin-cogs-metric-config-cancel"
                  }, " Cancel "))
                : _createCommentVNode("v-if", true)
            ])
          ]),
          _cache[22] || (_cache[22] = _createElementVNode("hr", { class: "mb-5" }, null, -1 /* CACHED */)),
          _createElementVNode("form", {
            id: "metric-configuration-form",
            onSubmit: _withModifiers(handleSave, ["prevent"]),
            class: "mb-4",
            "data-testid": "margin-cogs-metric-config-form"
          }, [
            _createElementVNode("div", _hoisted_7, [
              _cache[16] || (_cache[16] = _createElementVNode("label", null, "Metric Name:", -1 /* CACHED */)),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((name).value = $event)),
                type: "text",
                class: "form-control",
                required: "",
                maxlength: "100",
                minlength: "2",
                "data-testid": "margin-cogs-metric-config-name"
              }, null, 512 /* NEED_PATCH */), [
                [
                  _vModelText,
                  name.value,
                  void 0,
                  { trim: true }
                ]
              ]),
              _createElementVNode("div", _hoisted_8, _toDisplayString(nameError.value), 1 /* TEXT */)
            ]),
            _createElementVNode("div", _hoisted_9, [
              _cache[17] || (_cache[17] = _createElementVNode("label", null, "Metric Description:", -1 /* CACHED */)),
              _withDirectives(_createElementVNode("textarea", {
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((description).value = $event)),
                class: "form-control",
                "data-testid": "margin-cogs-metric-config-description"
              }, null, 512 /* NEED_PATCH */), [
                [
                  _vModelText,
                  description.value,
                  void 0,
                  { trim: true }
                ]
              ])
            ]),
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, [
                _cache[19] || (_cache[19] = _createElementVNode("label", {
                  for: "revenue",
                  class: "form-label"
                }, "Measured Revenue:", -1 /* CACHED */)),
                _withDirectives(_createElementVNode("select", {
                  id: "revenue",
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((revenueId).value = $event)),
                  class: "form-control",
                  "data-testid": "margin-cogs-metric-config-revenue"
                }, [
                  _cache[18] || (_cache[18] = _createElementVNode("option", { value: null }, "Total", -1 /* CACHED */)),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(rm).Revenues, (r) => {
                    return (_openBlock(), _createElementBlock("option", {
                      key: r.Id,
                      value: r.Id
                    }, _toDisplayString(r.Name), 9 /* TEXT, PROPS */, _hoisted_12))
                  }), 128 /* KEYED_FRAGMENT */))
                ], 512 /* NEED_PATCH */), [
                  [_vModelSelect, revenueId.value]
                ])
              ]),
              _createElementVNode("div", _hoisted_13, [
                _cache[21] || (_cache[21] = _createElementVNode("label", { for: "denomination" }, "Currency denomination:", -1 /* CACHED */)),
                _withDirectives(_createElementVNode("select", {
                  id: "denomination",
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((denomination).value = $event)),
                  class: "form-control",
                  "data-testid": "margin-cogs-metric-config-denomination"
                }, [...(_cache[20] || (_cache[20] = [
                  _createElementVNode("option", { value: "Unit" }, "Unit", -1 /* CACHED */),
                  _createElementVNode("option", { value: "Thousand" }, "Thousand", -1 /* CACHED */),
                  _createElementVNode("option", { value: "Million" }, "Million", -1 /* CACHED */)
                ]))], 512 /* NEED_PATCH */), [
                  [_vModelSelect, denomination.value]
                ])
              ])
            ]),
            _createVNode(MetricTimeWindowSettings, {
              startOffset: windowStartOffsetInMonths.value,
              "onUpdate:startOffset": _cache[7] || (_cache[7] = ($event: any) => ((windowStartOffsetInMonths).value = $event)),
              length: windowLengthInMonths.value,
              "onUpdate:length": _cache[8] || (_cache[8] = ($event: any) => ((windowLengthInMonths).value = $event))
            }, null, 8 /* PROPS */, ["startOffset", "length"]),
            _createVNode(MetricScheduleDependentSettings, {
              canDisable: true,
              isScheduleDependent: isScheduleDependent.value,
              "onUpdate:isScheduleDependent": _cache[9] || (_cache[9] = ($event: any) => ((isScheduleDependent).value = $event)),
              defaultProjectStartDate: defaultProjectStartDate.value,
              "onUpdate:defaultProjectStartDate": _cache[10] || (_cache[10] = ($event: any) => ((defaultProjectStartDate).value = $event)),
              startDateBoundary: startDateBoundary.value,
              "onUpdate:startDateBoundary": _cache[11] || (_cache[11] = ($event: any) => ((startDateBoundary).value = $event)),
              endDateBoundary: endDateBoundary.value,
              "onUpdate:endDateBoundary": _cache[12] || (_cache[12] = ($event: any) => ((endDateBoundary).value = $event)),
              applyBenefitsConfidenceFactor: applyBenefitsConfidenceFactor.value,
              "onUpdate:applyBenefitsConfidenceFactor": _cache[13] || (_cache[13] = ($event: any) => ((applyBenefitsConfidenceFactor).value = $event)),
              valueCommitmentType: valueCommitmentType.value,
              "onUpdate:valueCommitmentType": _cache[14] || (_cache[14] = ($event: any) => ((valueCommitmentType).value = $event))
            }, null, 8 /* PROPS */, ["isScheduleDependent", "defaultProjectStartDate", "startDateBoundary", "endDateBoundary", "applyBenefitsConfidenceFactor", "valueCommitmentType"])
          ], 32 /* NEED_HYDRATION */)
        ]))
      : _createCommentVNode("v-if", true)
  ]))
}
}

})